import type { ObjectDirective } from 'vue';

import { useServiceContainer } from '~/container';

interface StickyPayload {
    placeholder?: boolean;
    stickyAbove?: string;
}
interface StickyElement {
    element: HTMLElement;
    isStuck: boolean;
    shouldHavePlaceholder: boolean;
    placeholderElement?: HTMLElement;
    start: number;
    originalPosition?: string;
}

// Used for placeholder id
let index = 1;

// References
let onScrollReference: EventListener | null;
let recheckPositionsReference: EventListener | null;

export const Sticky: ObjectDirective<HTMLImageElement, StickyPayload> = {
    mounted(el, binding): void {
        const { ElementHelperService } = useServiceContainer();

        const placeholder = binding.value?.placeholder ?? false;
        const stickyAbove = binding.value?.stickyAbove;
        const stickyElement: StickyElement = {
            element: el,
            isStuck: false,
            shouldHavePlaceholder: placeholder,
            start: ElementHelperService.getOffsetTop(el),
        };

        // Set references
        onScrollReference = onScroll;
        recheckPositionsReference = recheckPositions;

        // Set listeners
        window.addEventListener('scroll', onScrollReference);
        window.addEventListener('resize', recheckPositionsReference);

        function recheckPositions() {
            if (!stickyElement.isStuck) {
                stickyElement.start = ElementHelperService.getOffsetTop(stickyElement.element);
            } else if (stickyElement.placeholderElement) {
                stickyElement.start = ElementHelperService.getOffsetTop(stickyElement.placeholderElement);
            }
        }

        function onScroll() {
            const pos = window.scrollY;
            let top = 0;

            if (stickyAbove && pos > stickyElement.start) {
                const bottomHeight = ElementHelperService.outerHeight(document.querySelector(stickyAbove)!, true);
                const bodyHeight = ElementHelperService.height(document.querySelector('body')!);

                if (pos + ElementHelperService.height(stickyElement.element) > bodyHeight - bottomHeight) {
                    top = bodyHeight - bottomHeight - pos - ElementHelperService.height(stickyElement.element);
                }
            }

            stickyElement.element.style.top = `${top}px`;

            if (!stickyElement.isStuck && pos > stickyElement.start) {
                stickyElement.originalPosition = stickyElement.element.style.position;

                stickyElement.element.classList.add('stuck');
                stickyElement.element.style.position = 'fixed';
                stickyElement.element.style.top = `${top}px`;

                stickyElement.isStuck = true;

                if (stickyElement.shouldHavePlaceholder) {
                    const placeholderId = getFormattedId(index);
                    stickyElement.placeholderElement = document.createElement('div');
                    stickyElement.placeholderElement.id = placeholderId;

                    el.dataset.placeholderElementId = placeholderId;

                    index++;

                    stickyElement.placeholderElement.style.height = `${ElementHelperService.outerHeight(
                        stickyElement.element,
                    )}px`;

                    const parent = stickyElement.element.parentNode;
                    parent!.insertBefore(stickyElement.placeholderElement, stickyElement.element);
                }
            } else if (stickyElement.isStuck && pos < stickyElement.start) {
                stickyElement.element.classList.remove('stuck');
                stickyElement.element.style.position = stickyElement.originalPosition || 'static';
                stickyElement.isStuck = false;

                if (stickyElement.placeholderElement) {
                    stickyElement.placeholderElement.parentNode!.removeChild(stickyElement.placeholderElement);
                    stickyElement.placeholderElement = undefined;
                }
            }
        }
    },
    beforeUnmount(el) {
        // Remove listeners
        window.removeEventListener('scroll', onScrollReference!);
        window.removeEventListener('resize', recheckPositionsReference!);

        // Remove created placeholder element on unmount
        const placeholderElementId = el.dataset.placeholderElementId;
        const placeholderElement = placeholderElementId ? document.getElementById(placeholderElementId) : null;

        placeholderElement?.remove();

        // Remove references to clear potential memory leaks
        onScrollReference = null;
        recheckPositionsReference = null;
    },
};

function getFormattedId(id: number) {
    return `stickyPlaceholder-${id}`;
}
